import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Smile, Search, Loader2 } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

// Tenor API
const TENOR_API_KEY = process.env.REACT_APP_TENOR_API_KEY || '';
const TENOR_API_URL = 'https://tenor.googleapis.com/v2';

const GifPicker = ({ onSelect }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [gifs, setGifs] = useState([]);
  const [stickers, setStickers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const searchGifs = async (query, isSticker = false) => {
    if (!query.trim()) {
      // Load trending if no query
      loadTrending(isSticker);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `${TENOR_API_URL}/search?q=${encodeURIComponent(query)}&key=${TENOR_API_KEY}&limit=20&contentfilter=high${isSticker ? '&searchfilter=sticker' : ''}`
      );
      const data = await response.json();
      
      const results = data.results?.map(result => ({
        id: result.id,
        url: result.media_formats.tinygif?.url || result.media_formats.gif?.url,
        previewUrl: result.media_formats.tinygif?.url || result.media_formats.nanogif?.url,
        title: result.content_description || 'GIF'
      })) || [];

      if (isSticker) {
        setStickers(results);
      } else {
        setGifs(results);
      }
    } catch (error) {
      console.error('Failed to search GIFs:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTrending = async (isSticker = false) => {
    setLoading(true);
    try {
      const response = await fetch(
        `${TENOR_API_URL}/featured?key=${TENOR_API_KEY}&limit=20&contentfilter=high${isSticker ? '&searchfilter=sticker' : ''}`
      );
      const data = await response.json();
      
      const results = data.results?.map(result => ({
        id: result.id,
        url: result.media_formats.tinygif?.url || result.media_formats.gif?.url,
        previewUrl: result.media_formats.tinygif?.url || result.media_formats.nanogif?.url,
        title: result.content_description || 'GIF'
      })) || [];

      if (isSticker) {
        setStickers(results);
      } else {
        setGifs(results);
      }
    } catch (error) {
      console.error('Failed to load trending:', error);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (open && gifs.length === 0) {
      loadTrending(false);
    }
  }, [open]);

  const handleSelect = (gif) => {
    onSelect(gif.url);
    setOpen(false);
    setSearchQuery('');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    searchGifs(searchQuery, false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" data-testid="gif-picker-trigger">
          <Smile className="w-5 h-5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96" data-testid="gif-picker-content">
        <Tabs defaultValue="gifs" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="gifs" onClick={() => searchQuery && searchGifs(searchQuery, false)}>
              GIFs
            </TabsTrigger>
            <TabsTrigger value="stickers" onClick={() => {
              if (stickers.length === 0) loadTrending(true);
              else if (searchQuery) searchGifs(searchQuery, true);
            }}>
              Stickers
            </TabsTrigger>
          </TabsList>

          <form onSubmit={handleSearch} className="relative mt-3 mb-2">
            <Search className="absolute left-3 top-3 w-4 h-4 text-[#94A3B8]" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search GIFs or Stickers..."
              className="pl-10"
              data-testid="gif-search-input"
            />
          </form>

          <TabsContent value="gifs">
            <ScrollArea className="h-80">
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-[#4338CA]" />
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {gifs.map((gif) => (
                    <img
                      key={gif.id}
                      src={gif.previewUrl}
                      alt={gif.title}
                      className="w-full h-32 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => handleSelect(gif)}
                      data-testid={`gif-${gif.id}`}
                    />
                  ))}
                </div>
              )}
              {!loading && gifs.length === 0 && (
                <p className="text-center text-[#94A3B8] py-8">
                  {searchQuery ? 'No GIFs found' : 'Type to search GIFs'}
                </p>
              )}
            </ScrollArea>
          </TabsContent>

          <TabsContent value="stickers">
            <ScrollArea className="h-80">
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-[#4338CA]" />
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  {stickers.map((sticker) => (
                    <img
                      key={sticker.id}
                      src={sticker.previewUrl}
                      alt={sticker.title}
                      className="w-full h-24 object-contain cursor-pointer hover:scale-110 transition-transform"
                      onClick={() => handleSelect(sticker)}
                      data-testid={`sticker-${sticker.id}`}
                    />
                  ))}
                </div>
              )}
              {!loading && stickers.length === 0 && (
                <p className="text-center text-[#94A3B8] py-8">
                  {searchQuery ? 'No stickers found' : 'Loading stickers...'}
                </p>
              )}
            </ScrollArea>
          </TabsContent>
        </Tabs>

        <p className="text-xs text-[#94A3B8] text-center mt-2">
          Powered by Tenor
        </p>
      </PopoverContent>
    </Popover>
  );
};

export default GifPicker;
